import {AfterViewInit, Component, ElementRef, ViewChild} from '@angular/core';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import {MatSort} from '@angular/material/sort';
import {IdexService} from '../../idex.service';
import {Toast} from '../../shared/toast/toast';
import {TranslateService} from '@ngx-translate/core';
import {AppLoaderComponent} from '../../shared/app-loader/app-loader.component';
import {PaginatorComponent} from '../../shared/paginator/paginator.component';
import {RestDataSource} from '../../shared/rest-data-source';
import {ConfirmDialogComponent} from '../../e-signature/confirm-dialog/confirm-dialog.component';
import {AllowIpDialogComponent} from './allow-ip-dialog.component';

@Component({
   selector: 'app-allow-ip',
   templateUrl: './allow-ip.component.html',
   styleUrls: ['./allow-ip.component.css']
})
export class AllowIpComponent implements AfterViewInit {
   @ViewChild(PaginatorComponent) paginator: PaginatorComponent;
   @ViewChild(MatSort) sort: MatSort;
   @ViewChild(AppLoaderComponent) loader: AppLoaderComponent;
   @ViewChild('wrapper') wrapper: ElementRef;
   displayedColumns = ['ipAddress', 'isPrefix', 'description', 'updateBy', 'updateDate', 'actions'];
   dataSource = new RestDataSource<any>();
   siteCode: string;
   loaded = false;
   searching = '';
   searchFocused: boolean;
   preventBlur: boolean;
   searchResult: any[];
   searchSize = 8;
   private searchTimer: any;

   constructor(public service: IdexService, public toast: Toast, public dialog: MatDialog,
               private translate: TranslateService) {
   }

   ngAfterViewInit(): void {
      this.siteCode = window.localStorage.getItem('tin');
      this.getData();
   }

   // Search works like the other admin list pages (site-list, forms): typing only refreshes
   // the preview dropdown, while Enter or picking a suggestion applies the term to the table.
   // RestDataSource sends it as the `searchText` param and resets to the first page.
   onSearch(value: string): void {
      this.searchFocused = false;
      if (this.searchTimer) { clearTimeout(this.searchTimer); }
      this.dataSource.filter = (value || '').trim();
   }

   onSearchChange(input: any, e: any): void {
      if (e.keyCode !== 13) { this.searchFocused = true; }
      this.searchResult = null;
      this.searchChange(input);
   }

   onSearchFocus(input: any): void {
      this.searchFocused = true;
      this.searchChange(input);
   }

   onSearchBlur(): void {
      // Delay so a click on a suggestion still registers before the dropdown closes.
      setTimeout(() => {
         if (!this.preventBlur) { this.searchFocused = false; }
      }, 300);
    }

   // Debounced peek at the first few matches, shown under the input.
   private searchChange(input: any): void {
      if (this.searchTimer) { clearTimeout(this.searchTimer); }
      const kw = (input.value || '').trim();
      if (kw.length === 0) { return; }
      this.searchTimer = setTimeout(() => {
         this.searchTimer = null;
         this.service.listAllowedIp(this.siteCode, {searchText: kw, page: 0, size: this.searchSize})
            .subscribe((data: any) => this.searchResult = data.content || [],
               () => this.searchResult = []);
      }, 300);
   }

   // Picking a suggestion opens its edit dialog directly, same as the Contacts page
   // (highlight()) -- searching for a specific entry should take you straight to editing it
   // instead of just filtering the table down to it.
   selectSearchItem(item: any): void {
      setTimeout(() => this.searchFocused = false, 100);
      this.open(item);
   }

   // Secondary line of a suggestion: the prefix flag and the description, whichever are set.
   suggestionDesc(item: any): string {
      const parts = [];
      if (item.isPrefix) { parts.push(this.translate.instant('company.allow-match-prefix')); }
      if (item.description) { parts.push(item.description); }
      return parts.join(' : ');
   }

   // Paging and sorting are done on the server: RestDataSource emits page/size/sort
   // as query params and the backend returns a Spring Page ({content, totalElements}).
   getData(): void {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
      // Force the initial sort (newest first). Setting matSortDirection in the template
      // alone is not reliably picked up by RestDataSource on the first load.
      if (this.sort) {
         this.sort.active = 'updateDate';
         this.sort.direction = 'desc';
      }
      this.dataSource.changes.subscribe((params: any) => {
         this.loader.show();
         // Tie-breaker: when rows share the same value in the active sort column, order them by
         // the most recent update. Skipped when the primary sort already is updateDate.
         const query = (this.sort && this.sort.active && this.sort.active !== 'updateDate')
            ? params.append('sort', 'updateDate,desc')
            : params;
         this.service.listAllowedIp(this.siteCode, query).subscribe((data: any) => {
            data.content = (data.content || []).map((it: any) => ({...it, updateDate: this.toDate(it.updateDate)}));
            this.dataSource.loaded(data);
            this.loaded = true;
            if (this.wrapper) { this.wrapper.nativeElement.scrollTop = 0; }
         }, () => {}).add(() => this.loader.hide());
      });
      setTimeout(() => this.dataSource.loadData());
   }

   // Java LocalDateTime is serialized as an array [y, M, d, h, m, s, nano]; convert to a real Date.
   private toDate(a: any): any {
      if (Array.isArray(a)) {
         return new Date(a[0], (a[1] || 1) - 1, a[2] || 1, a[3] || 0, a[4] || 0, a[5] || 0);
      }
      return a;
   }

   open(item?: any): void {
      const dialogRef = this.dialog.open(AllowIpDialogComponent, {disableClose: true, width: '420px'});
      dialogRef.componentInstance.init(this.siteCode, item);
      dialogRef.afterClosed().subscribe(result => {
         if (result) {
            this.translate.get('company.allow-save-success').subscribe(s => this.toast.success(s));
            this.dataSource.loadData();
         }
      });
   }

   confirmDelete(item: any): void {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {disableClose: true, data: this});
      dialogRef.componentInstance.messageWarning = this.translate.instant('company.allow-delete-ip');
      dialogRef.componentInstance.isMsg = true;
      dialogRef.afterClosed().subscribe(result => {
         if (result) {
            this.service.deleteAllowedIp(this.siteCode, item.id).subscribe(() => {
               this.toast.success('Deleted successfully');
               this.dataSource.loadData();
            }, (e: any) => this.toast.error(e && e.message ? e.message : 'Delete failed'));
         }
      });
   }
}
